/* ============================================================
   Karibu PDP Blocks — JavaScript Custom Elements
   ============================================================ */

'use strict';

/* ============================================================
   Utilities
   ============================================================ */

function formatMoney(cents) {
  return (cents / 100).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });
}

/* ============================================================
   <accessory-configurator>
   Wrapper um alle Zubehör-Inputs. Feuert 'karibu:change'-Events
   wenn User eine Auswahl trifft.
   ============================================================ */

class AccessoryConfigurator extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this._handleChange.bind(this));

    // URL → DOM: Deep-Link-Konfiguration rehydrieren
    this._hydrateFromURL();

    // Initiales Update: Preselected/disabled Items sofort zählen
    requestAnimationFrame(() => {
      this.querySelectorAll('collapsible-expandable').forEach(accordion => {
        this._updateAccordionSummary(accordion);
      });
      this.querySelectorAll('.karibu-configurator-section').forEach(section => {
        this._updateSectionSummary(section);
      });
      this._updateItemPrices();
      this._syncURL();
    });
  }

  _hydrateFromURL() {
    const params = new URL(window.location.href).searchParams;
    for (const [key, value] of params.entries()) {
      if (!key.startsWith('accessory-')) continue;
      const variantId = key.slice('accessory-'.length);
      if (!variantId) continue;

      const input = this.querySelector(`input[value="${CSS.escape(variantId)}"]`);
      if (!input) continue;
      if (input.disabled) continue;

      input.checked = true;
      const item = input.closest('.karibu-config-item');
      if (!item) continue;
      item.classList.add('is-selected');

      const qtyInput = item.querySelector('input[name="quantity"]');
      if (qtyInput) {
        const parsed = parseInt(value, 10);
        qtyInput.value = isNaN(parsed) || parsed < 1 ? 1 : parsed;
      }
    }
  }

  _syncURL() {
    const url = new URL(window.location.href);
    for (const k of [...url.searchParams.keys()]) {
      if (k.startsWith('accessory-')) url.searchParams.delete(k);
    }

    this.querySelectorAll('.karibu-config-item').forEach(item => {
      const checked = item.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked');
      if (!checked) return;
      const variantId = checked.value;
      if (!variantId) return;

      const qtyInput = item.querySelector('input[name="quantity"]');
      const qty = qtyInput ? parseInt(qtyInput.value || '1', 10) : 1;
      if (qty <= 0) return;

      url.searchParams.append('accessory-' + variantId, String(qty));
    });

    window.history.replaceState({}, '', url.toString());
  }

  _handleChange(e) {
    const input = e.target;

    // Reagiere auf Checkbox/Radio UND Quantity-Input
    const isSelection = input.matches('input[type="checkbox"], input[type="radio"]');
    const isQuantity = input.matches('input[name="quantity"]');
    if (!isSelection && !isQuantity) return;

    if (isSelection) {
      // Aktive CSS-Klasse auf Item setzen
      const item = input.closest('.karibu-config-item');
      if (!item) return;

      if (input.type === 'checkbox') {
        item.classList.toggle('is-selected', input.checked);
        // Sync quantity: check → 1, uncheck → 0
        const qtyInput = item.querySelector('input[name="quantity"]');
        if (qtyInput) {
          qtyInput.value = input.checked ? Math.max(parseInt(qtyInput.min || '1', 10), 1) : 0;
        }
      } else {
        const groupName = input.name;
        this.querySelectorAll(`input[type="radio"][name="${CSS.escape(groupName)}"]`).forEach(radio => {
          const radioItem = radio.closest('.karibu-config-item');
          if (radioItem) radioItem.classList.toggle('is-selected', radio.checked);
        });
      }
    }

    // Alle Accordions in diesem Configurator aktualisieren
    this.querySelectorAll('collapsible-expandable').forEach(accordion => {
      this._updateAccordionSummary(accordion);
    });

    // Sektions-Summaries aktualisieren (Zubehör / Montage)
    this.querySelectorAll('.karibu-configurator-section').forEach(section => {
      this._updateSectionSummary(section);
    });

    // Angezeigte Preise auf den Cards aktualisieren (Menge × Einzelpreis)
    this._updateItemPrices();

    this.dispatchEvent(new CustomEvent('karibu:change', { bubbles: true }));

    // DOM → URL: aktuellen Konfigurator-Zustand spiegeln
    this._syncURL();
  }

  _updateItemPrices() {
    this.querySelectorAll('.karibu-config-item').forEach(item => {
      const checked = item.querySelector('input[type="checkbox"], input[type="radio"]');
      if (!checked) return;

      const unitPrice = parseInt(checked.dataset.price || '0', 10);
      if (unitPrice === 0) return;

      const qtyInput = item.querySelector('input[name="quantity"]');
      const qty = qtyInput ? parseInt(qtyInput.value || '0', 10) : 1;
      const lineTotal = unitPrice * qty;

      const priceEl = item.querySelector('.karibu-config-item-price:not(.is-free)');
      if (priceEl) {
        priceEl.textContent = lineTotal > 0 ? '+' + formatMoney(lineTotal) : '+' + formatMoney(unitPrice);
      }
    });
  }

  _updateAccordionSummary(accordion) {
    const totalCount = parseInt(accordion.dataset.totalCount || '0', 10);
    let selectedCount = 0;
    let sum = 0;

    accordion.querySelectorAll('.karibu-config-item').forEach(item => {
      const checked = item.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked');
      if (!checked) return;
      selectedCount++;

      const price = parseInt(checked.dataset.price || '0', 10);
      const qtyInput = item.querySelector('input[name="quantity"]');
      const qty = qtyInput ? parseInt(qtyInput.value || '1', 10) : 1;
      sum += price * qty;
    });

    const countEl = accordion.querySelector('[data-accordion-count]');
    if (countEl) {
      countEl.textContent = '(' + selectedCount + '/' + totalCount + ')';
    }

    const priceSummary = accordion.querySelector('[data-accordion-price]');
    if (priceSummary) {
      priceSummary.textContent = sum > 0 ? '+ ' + formatMoney(sum) : '';
    }
  }

  _updateSectionSummary(section) {
    let selectedCount = 0;
    let sum = 0;

    section.querySelectorAll('.karibu-config-item').forEach(item => {
      const checked = item.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked');
      if (!checked) return;
      selectedCount++;

      const price = parseInt(checked.dataset.price || '0', 10);
      const qtyInput = item.querySelector('input[name="quantity"]');
      const qty = qtyInput ? parseInt(qtyInput.value || '1', 10) : 1;
      sum += price * qty;
    });

    const countEl = section.querySelector('[data-section-count]');
    if (countEl) {
      countEl.textContent = selectedCount > 0 ? selectedCount + ' ausgewählt' : '';
    }

    const priceEl = section.querySelector('[data-section-price]');
    if (priceEl) {
      priceEl.textContent = sum > 0 ? '+ ' + formatMoney(sum) : '';
    }
  }
}

customElements.define('accessory-configurator', AccessoryConfigurator);

/* ============================================================
   <total-price-with-accessories>
   Hört auf karibu:change und aktualisiert den Gesamtpreis.
   Rendert außerdem die Extras-Liste in der Buy Box.
   ============================================================ */

class TotalPriceWithAccessories extends HTMLElement {
  connectedCallback() {
    this._lastTotal = 0;
    this._boundUpdate = this._update.bind(this);
    document.addEventListener('karibu:change', this._boundUpdate);

    // Toggle-Button für Cost Breakdown
    const toggle = this.querySelector('.karibu-buy-box-toggle');
    const breakdown = this.querySelector('[data-breakdown]');
    if (toggle && breakdown) {
      toggle.addEventListener('click', () => {
        const isOpen = breakdown.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(isOpen));
        if (isOpen) breakdown.removeAttribute('hidden');
      });
    }

    // Initiales Update nach DOM-Ready
    requestAnimationFrame(() => this._update());
  }

  disconnectedCallback() {
    document.removeEventListener('karibu:change', this._boundUpdate);
  }

  _update() {
    const basePrice = parseInt(this.dataset.basePrice || '0', 10);
    let extraTotal = 0;
    const extraItems = [];

    // Alle aktiven Accessory-Inputs einsammeln
    document.querySelectorAll('accessory-configurator .karibu-config-item').forEach(item => {
      const checked = item.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked');
      if (!checked) return;

      const price = parseInt(checked.dataset.price || '0', 10);
      const qtyInput = item.querySelector('input[name="quantity"]');
      const qty = qtyInput ? parseInt(qtyInput.value || '0', 10) : 1;
      const lineTotal = price * qty;

      if (lineTotal > 0) {
        extraTotal += lineTotal;
        const label = item.querySelector('.karibu-config-item-name')?.textContent?.trim();
        if (label) {
          extraItems.push({ label, price: lineTotal, qty });
        }
      }
    });

    const total = basePrice + extraTotal;

    // Gesamtpreis aktualisieren + Animation
    const priceEl = this.querySelector('.karibu-buy-box-price');
    if (priceEl) {
      const newText = formatMoney(total);
      if (priceEl.textContent !== newText) {
        priceEl.textContent = newText;
        // Price pulse animation
        if (this._lastTotal !== 0 && this._lastTotal !== total) {
          priceEl.classList.remove('is-animating');
          void priceEl.offsetWidth; // force reflow
          priceEl.classList.add('is-animating');
        }
      }
    }
    this._lastTotal = total;

    // Header-Summary aktualisieren
    const summaryEl = this.querySelector('[data-config-summary]');
    if (summaryEl) {
      if (extraItems.length > 0) {
        summaryEl.textContent = `Basis + ${extraItems.length} Zubehör`;
      } else {
        summaryEl.textContent = 'Basis';
      }
    }

    // Cost Breakdown aktualisieren
    const breakdownInner = this.querySelector('.karibu-buy-box-breakdown-inner');
    if (breakdownInner) {
      let html = `
        <div class="karibu-buy-box-breakdown-item">
          <span>Grundpreis</span>
          <span>${formatMoney(basePrice)}</span>
        </div>`;

      if (extraItems.length > 0) {
        html += extraItems.map(i => `
        <div class="karibu-buy-box-breakdown-item">
          <span>${i.label}${i.qty > 1 ? ' ×' + i.qty : ''}</span>
          <span>+${formatMoney(i.price)}</span>
        </div>`).join('');

        html += `
        <div class="karibu-buy-box-breakdown-divider"></div>
        <div class="karibu-buy-box-breakdown-total">
          <span>Gesamt</span>
          <span>${formatMoney(total)}</span>
        </div>`;
      }

      breakdownInner.innerHTML = html;
    }

    // Legacy: Versteckter Container für Extras
    const extrasList = this.querySelector('.karibu-buy-box-extras');
    if (extrasList) extrasList.innerHTML = '';
  }
}

customElements.define('total-price-with-accessories', TotalPriceWithAccessories);

/* ============================================================
   <add-to-cart>
   Fügt Hauptprodukt + alle ausgewählten Accessories zum 
   Warenkorb hinzu via Cart API.
   ============================================================ */

class AddToCart extends HTMLElement {
  connectedCallback() {
    this._btn = this.querySelector('.karibu-atc-btn');
    if (this._btn) {
      this._btn.addEventListener('click', this._handleClick.bind(this));
    }

    // Sync variant ID wenn der User die Variante im PDP ändert
    document.addEventListener('product:variant-change', (e) => {
      const variant = e.detail?.variant;
      if (variant?.id) {
        this.dataset.variantId = variant.id;
      }
    });
  }

  async _handleClick(e) {
    e.preventDefault();
    const btn = this._btn;
    const mainVariantId = this.dataset.variantId;
    if (!mainVariantId) return;

    btn.classList.add('is-loading');
    btn.setAttribute('disabled', '');

    const items = [];

    // Hauptprodukt-Menge: aus dem Buy-Box-Stepper lesen (simple mode) oder default 1
    const buyBoxQtyInput = this.closest('total-price-with-accessories')
      ?.querySelector('[data-buy-box-quantity]');
    const mainQty = buyBoxQtyInput ? parseInt(buyBoxQtyInput.value || '1', 10) : 1;
    items.push({ id: parseInt(mainVariantId, 10), quantity: mainQty });

    // Aktuellen Warenkorb laden, um Gratis-Produkte zu prüfen
    let existingFreeIds = new Set();
    try {
      const cartRes = await fetch(window.Shopify.routes.root + 'cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (cartRes.ok) {
        const cart = await cartRes.json();
        cart.items.forEach(ci => {
          if (ci.original_price === 0) existingFreeIds.add(ci.variant_id);
        });
      }
    } catch (_) { /* ignore */ }

    // Alle angehakten Accessories einsammeln
    document.querySelectorAll('accessory-configurator input:checked').forEach(input => {
      const variantId = parseInt(input.value, 10);
      if (!variantId || isNaN(variantId)) return;

      // Gratis-Produkt bereits im Warenkorb → überspringen
      const price = parseInt(input.dataset.price || '0', 10);
      if (price === 0 && existingFreeIds.has(variantId)) return;

      // quantity aus dem zugehörigen stepper
      const item = input.closest('.karibu-config-item');
      let qty = 1;
      if (item) {
        const qtyInput = item.querySelector('input[name="quantity"]');
        if (qtyInput) qty = parseInt(qtyInput.value || '1', 10);
      }

      // Gratis-Produkte: gleiche Menge wie Hauptprodukt
      if (price === 0) qty = mainQty;

      // Items mit qty 0 überspringen
      if (qty <= 0) return;

      // Properties aus hidden fields
      const properties = {};
      const container = input.closest('[data-accessory-fieldset]') || item?.parentElement;
      if (container) {
        container.querySelectorAll('input[type="hidden"]').forEach(hidden => {
          const k = (hidden.dataset.propertyKey || hidden.name || '').replace('pre-selected', '').trim();
          if (k) properties[k] = hidden.value;
        });
      }

      // Parent-Referenz für nested Cart-Darstellung
      properties['_karibu_parent'] = mainVariantId;

      items.push({ id: variantId, quantity: qty, properties });
    });

    try {
      // Sections mitschicken damit Shopify die gerenderten Sektionen zurückgibt
      const sectionsToRender = this._getSectionsToRender();
      const payload = { items };
      if (sectionsToRender.length > 0) {
        payload.sections = sectionsToRender;
        payload.sections_url = window.location.pathname;
      }

      const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.description || errData.message || 'Cart error');
      }

      const cartData = await res.json();

      // Apex Theme: Mini-Cart Sektionen aktualisieren
      if (cartData.sections) {
        this._renderSections(cartData.sections);
      }

      // PubSub Event für Apex Theme (aktualisiert Cart-Counter etc.)
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'karibu-atc',
          cartData,
        });
      }

      // Mini-Cart / Cart Drawer öffnen
      document.dispatchEvent(new CustomEvent('open-mini-cart'));

      // Fallback events
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

    } catch (err) {
      console.error('[karibu-atc]', err);
      alert('Fehler beim Hinzufügen: ' + err.message);
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('disabled');
    }
  }

  _getSectionsToRender() {
    // Mini-Cart Sektionen des Apex Themes
    const sections = [];
    const header = document.getElementById('header');
    if (header?.dataset.id) sections.push(header.dataset.id);

    const miniCartContent = document.getElementById('header-mini-cart-content');
    if (miniCartContent) sections.push('header-mini-cart-content');

    const miniCartFooter = document.getElementById('header-mini-cart-footer');
    if (miniCartFooter) sections.push('header-mini-cart-footer');

    const mobileDock = document.getElementById('mobile-dock');
    if (mobileDock?.dataset.id) sections.push(mobileDock.dataset.id);

    return sections;
  }

  _renderSections(sections) {
    Object.entries(sections).forEach(([sectionId, html]) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Header cart link
      const header = document.getElementById('header');
      if (header?.dataset.id === sectionId) {
        const source = doc.querySelector('.head-slot-cart-link');
        const target = header.querySelector('.head-slot-cart-link');
        if (source && target) target.innerHTML = source.innerHTML;
      }

      // Mini-Cart content
      if (sectionId === 'header-mini-cart-content') {
        const el = document.getElementById('header-mini-cart-content');
        const source = doc.querySelector('.shopify-section');
        if (el && source) el.innerHTML = source.innerHTML;
      }

      // Mini-Cart footer
      if (sectionId === 'header-mini-cart-footer') {
        const el = document.getElementById('header-mini-cart-footer');
        const source = doc.querySelector('.shopify-section');
        if (el && source) el.innerHTML = source.innerHTML;
      }

      // Mobile dock
      const mobileDock = document.getElementById('mobile-dock');
      if (mobileDock?.dataset.id === sectionId) {
        const source = doc.querySelector('.head-slot-cart-link');
        const target = mobileDock.querySelector('.head-slot-cart-link');
        if (source && target) target.innerHTML = source.innerHTML;
      }
    });
  }
}

customElements.define('add-to-cart', AddToCart);

/* ============================================================
   <stepper-input>
   Mengen-Input mit +/- Buttons
   ============================================================ */

class StepperInput extends HTMLElement {
  connectedCallback() {
    this._input = this.querySelector('input[type="number"]');
    this._min = parseInt(this.getAttribute('min') || '0', 10);
    this._max = parseInt(this.getAttribute('max') || '99', 10);
    this._step = parseInt(this.getAttribute('step') || '1', 10);

    this.querySelector('[data-stepper-minus]')
      ?.addEventListener('click', () => this._change(-this._step));
    this.querySelector('[data-stepper-plus]')
      ?.addEventListener('click', () => this._change(+this._step));
  }

  _change(delta) {
    if (!this._input) return;
    const current = parseInt(this._input.value || '0', 10);
    const next = Math.min(this._max, Math.max(this._min, current + delta));
    this._input.value = next;

    // Sync checkbox: qty > 0 → check, qty === 0 → uncheck
    const item = this.closest('.karibu-config-item');
    if (item) {
      const checkbox = item.querySelector('input[type="checkbox"]');
      if (checkbox && !checkbox.disabled) {
        const shouldBeChecked = next > 0;
        if (checkbox.checked !== shouldBeChecked) {
          checkbox.checked = shouldBeChecked;
          item.classList.toggle('is-selected', shouldBeChecked);
        }
      }
    }

    this._input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

customElements.define('stepper-input', StepperInput);

/* ============================================================
   <karibu-modal>
   Leichtgewichtiges Detail-Modal für Zubehör-Produkte
   ============================================================ */

class KaribuModal extends HTMLElement {
  connectedCallback() {
    const modalId = this.dataset.modal;

    // Modal an body anhängen, damit es nicht im Stacking Context
    // eines Eltern-Elements (z.B. Akkordeon) gefangen ist
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    // Trigger-Buttons
    document.querySelectorAll(`[data-modal-target="${modalId}"]`).forEach(btn => {
      btn.addEventListener('click', () => this._open());
    });

    // Schließen (alle [data-modal-close] Buttons)
    this.querySelectorAll('[data-modal-close]').forEach(btn => {
      btn.addEventListener('click', () => this._close());
    });

    // Klick auf Overlay schließt
    this.addEventListener('click', e => { if (e.target === this) this._close(); });

    // Escape schließt
    this._escHandler = e => { if (e.key === 'Escape' && this.classList.contains('is-open')) this._close(); };
    document.addEventListener('keydown', this._escHandler);
  }

  disconnectedCallback() {
    if (this._escHandler) document.removeEventListener('keydown', this._escHandler);
  }

  _open() {
    this.classList.add('is-open');
    document.body.classList.add('karibu-modal-open');
    this._initGalleryDots();
  }

  _initGalleryDots() {
    if (this._dotsInitialized) return;
    const gallery = this.querySelector('[data-gallery]');
    const dotsContainer = this.querySelector('[data-gallery-dots]');
    if (!gallery || !dotsContainer) return;

    const dots = dotsContainer.querySelectorAll('.karibu-modal-dot');
    const items = gallery.querySelectorAll('.karibu-modal-gallery-item');
    if (dots.length === 0 || items.length === 0) return;

    // Click on dot → scroll to slide
    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.dotIndex, 10);
        items[idx]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      });
    });

    // IntersectionObserver → sync active dot
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = Array.from(items).indexOf(entry.target);
          dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
        }
      });
    }, { root: gallery, threshold: 0.6 });

    items.forEach(item => observer.observe(item));
    this._dotsInitialized = true;
  }

  _close() {
    this.classList.remove('is-open');
    // Nur scroll-lock entfernen wenn kein anderes Modal offen ist
    if (!document.querySelector('.karibu-modal-overlay.is-open')) {
      document.body.classList.remove('karibu-modal-open');
    }
  }
}

customElements.define('karibu-modal', KaribuModal);

/* ============================================================
   <sticky-atc-bar>
   Slim ATC bar — always visible except while the buy box is in view.
   ============================================================ */

class StickyAtcBar extends HTMLElement {
  connectedCallback() {
    this._btn = this.querySelector('.karibu-sticky-bar-atc');
    this._priceEl = this.querySelector('.karibu-sticky-bar-price');
    this._visible = false;

    // Find the buy box and its ATC button BEFORE moving to body
    this._buyBox = document.querySelector('.karibu-buy-box');
    if (!this._buyBox) return;
    this._ctaBtn = this._buyBox.querySelector('.karibu-atc-btn');
    if (!this._ctaBtn) return;

    // Move to body to escape stacking context of product-info column
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    // IntersectionObserver — bar is visible whenever the buy box ATC button is not fully in viewport
    this._observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          this._hide();
        } else {
          this._show();
        }
      },
      { threshold: 1, rootMargin: '0px' }
    );
    this._observer.observe(this._ctaBtn);

    // Watch for late-loading third-party widgets
    this._widgetObserver = new MutationObserver(() => {
      if (!this._visible) return;
      clearTimeout(this._widgetDebounce);
      this._widgetDebounce = setTimeout(() => {
        const h = this.offsetHeight;
        this._nudgeThirdPartyWidgets(true, h);
      }, 200);
    });
    this._widgetObserver.observe(document.body, { childList: true, subtree: true });

    // Remove initial aria-hidden after first frame (prevents flash)
    requestAnimationFrame(() => {
      this.removeAttribute('aria-hidden');
    });

    // Sync price with buy box
    this._syncHandler = this._syncPrice.bind(this);
    document.addEventListener('karibu:change', this._syncHandler);

    // ATC click → delegate to original button
    if (this._btn) {
      this._btn.addEventListener('click', this._handleAtcClick.bind(this));
    }
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this._widgetObserver) this._widgetObserver.disconnect();
    if (this._syncHandler) document.removeEventListener('karibu:change', this._syncHandler);
  }

  _show() {
    if (this._visible) return;
    this._visible = true;
    this.classList.add('is-visible');
    document.body.classList.add('sticky-bar-active');
    requestAnimationFrame(() => {
      const h = this.offsetHeight;
      document.documentElement.style.setProperty('--sticky-bar-offset', h + 'px');
      this._nudgeThirdPartyWidgets(true, h);
    });
  }

  _hide() {
    if (!this._visible) return;
    this._visible = false;
    this.classList.remove('is-visible');
    document.body.classList.remove('sticky-bar-active');
    document.documentElement.style.setProperty('--sticky-bar-offset', '0px');
    this._nudgeThirdPartyWidgets(false);
  }

  _syncPrice() {
    if (!this._priceEl || !this._buyBox) return;
    const buyBoxPrice = this._buyBox.querySelector('.karibu-buy-box-price');
    if (buyBoxPrice) {
      this._priceEl.textContent = buyBoxPrice.textContent;
    }
  }

  async _handleAtcClick(e) {
    e.preventDefault();
    // Delegate to original ATC button in buy box
    const originalBtn = this._ctaBtn;
    if (!originalBtn) return;

    // Mirror loading state
    this._btn.classList.add('is-loading');
    this._btn.setAttribute('disabled', '');

    // Click the original
    originalBtn.click();

    // Wait for original to finish loading
    const waitForDone = () => new Promise((resolve) => {
      const check = () => {
        if (!originalBtn.classList.contains('is-loading')) {
          resolve();
        } else {
          requestAnimationFrame(check);
        }
      };
      // Start checking after a tick
      requestAnimationFrame(check);
    });

    await waitForDone();
    this._btn.classList.remove('is-loading');
    this._btn.removeAttribute('disabled');
  }

  _nudgeThirdPartyWidgets(up, height = 70) {
    const transition = 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)';

    // Charles WhatsApp bubble
    const charles = document.querySelector('.satellite-bubble.chspt-wrapper');
    if (charles) {
      charles.style.transition = transition;
      charles.style.transform = up ? `translateY(-${height}px)` : '';
    }

    // Trusted Shops badge — find via stable ID prefix, then target fixed-position parent
    const tsBadge = document.querySelector('[id^="minimized-trustbadge"]');
    if (tsBadge) {
      const tsContainer = tsBadge.closest('[style*="position: fixed"]') || tsBadge.parentElement;
      if (tsContainer) {
        tsContainer.style.transition = transition;
        tsContainer.style.transform = up ? `translateY(-${height}px)` : '';
      }
    }
  }
}

customElements.define('sticky-atc-bar', StickyAtcBar);

/* ============================================================
   Akkordeon — leichtgewichtige Basis-Implementierung
   (falls <collapsible-expandable> noch nicht vorhanden)
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.karibu-accordion-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.karibu-accordion-item');
      if (!item) return;
      item.classList.toggle('is-open');
      const expanded = item.classList.contains('is-open');
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  });
});
